import { useEffect, useMemo, useState } from 'react';
import { Text } from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import useGetAllPortfolioList from '@hooks/useGetAllPortfolioList';
import DualListSelectDialog, { type DualListItem } from '@components/DualListSelectDialog';

import type { TeamType } from '../types';
import useGetTeamPortfolios from '../hooks/useGetTeamPortfolios';

interface Props {
  team: TeamType;
  handleClose: () => void;
  afterSubmit: () => void;
}

const UpdatePortfoliosDialog = ({ team, handleClose, afterSubmit }: Props) => {
  const { getPortfolios, portfolios, loading: allPortfoliosLoading } = useGetAllPortfolioList();
  const { getTeamPortfolios, portfolios: teamPortfolios, loading: teamPortfoliosLoading } = useGetTeamPortfolios();

  const [errors, setErrors] = useState<FormErrors | null>(null);

  useEffect(() => {
    getPortfolios();
    getTeamPortfolios(team.id);
  }, [getPortfolios, getTeamPortfolios, team]);

  const items = useMemo<DualListItem[]>(
    () => portfolios.map((p) => ({ id: p.id, label: p.name })),
    [portfolios]
  );

  const initialSelectedIds = useMemo(
    () => teamPortfolios.map((p) => p.id),
    [teamPortfolios]
  );

  const handleSave = async (added: string[], removed: string[]) => {
    try {
      const response = await api.patch(`/teams/${team.id}/portfolios`, {
        add: added,
        remove: removed,
      });

      if (response?.status === 200) {
        toast.success('Portfólios da equipe atualizados com sucesso.');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
        throw new Error('Validation errors');
      }
    } catch (error) {
      if (!(error instanceof Error && error.message === 'Validation errors')) {
        toast.error('Erro ao atualizar os portfólios da equipe. Verifique os dados e tente novamente.');
        datadogRum.addError(new Error('Fail to update team portfolios'), {
          error,
          teamId: team.id,
          added,
          removed,
        });
      }
      throw error;
    }
  };

  return (
    <DualListSelectDialog
      onClose={handleClose}
      title="Gerenciar portfólios"
      subtitle={
        <Text color="fg.muted">
          Equipe <Text as="span" fontWeight="medium">{team.name}</Text>
        </Text>
      }
      items={items}
      initialSelectedIds={initialSelectedIds}
      onSave={handleSave}
      loading={allPortfoliosLoading || teamPortfoliosLoading}
      errors={errors}
      labels={{
        defaultGroup: 'Portfólios',
        availableSubtitle: 'Selecione um ou vários portfólios',
        selectedSubtitle: 'Remova rapidamente o que não pertence a essa equipe',
        emptyAvailableTitle: 'Nenhum portfólio encontrado',
        emptyAvailableDescription: 'Tente outro termo de busca ou revise os filtros aplicados.',
        emptySelectedTitle: 'Nenhum portfólio selecionado',
        emptySelectedDescription: 'Escolha itens da coluna à esquerda para adicioná-los à equipe.',
      }}
    />
  );
};

export default UpdatePortfoliosDialog;
